// =============================================================================
// Helper centralisé pour transformer les erreurs Supabase / RLS / réseau
// en messages clairs et explicatifs en français.
//
// Objectif : quand un utilisateur déclenche une action qu'il n'a pas le droit
// d'exécuter, il doit comprendre POURQUOI (rôle, accès projet, propriétaire,
// etc.) — pas voir un code d'erreur Postgres brut.
// =============================================================================

import { useActionErrorStore, type ActionErrorKind } from '@/components/shared/ActionErrorDialog';

export type ActionContext =
  // Projets
  | 'project.create'
  | 'project.update'
  | 'project.delete'
  | 'project.move'
  | 'project.reorder'
  // Colonnes Kanban
  | 'status.create'
  | 'status.update'
  | 'status.delete'
  | 'status.reorder'
  // Tâches
  | 'task.create'
  | 'task.update'
  | 'task.delete'
  | 'task.move'
  // Membres / espaces
  | 'member.manage'
  | 'space.manage'
  // Documents
  | 'document.create'
  | 'document.update'
  | 'document.delete'
  // Wiki
  | 'wiki.create'
  | 'wiki.update'
  | 'wiki.delete'
  // Timesheets
  | 'timesheet.create'
  | 'timesheet.update'
  | 'timesheet.delete'
  | 'timesheet.approve'
  | 'timesheet.reject'
  // Planning / événements
  | 'event.create'
  | 'event.update'
  | 'event.delete'
  | 'event.rsvp'
  // Chat
  | 'chat.message.send'
  | 'chat.message.update'
  | 'chat.message.delete'
  | 'chat.channel.manage'
  // Équipes
  | 'team.manage'
  // Budget / dépenses
  | 'expense.create'
  | 'expense.update'
  | 'expense.delete'
  // Invitations
  | 'invitation.send'
  | 'invitation.revoke'
  // Rôles & permissions
  | 'role.assign'
  | 'permission.update'
  // Générique
  | 'generic';

interface NormalizedError {
  title: string;
  description: string;
  kind: ActionErrorKind;
}

const ACTION_LABELS: Partial<Record<ActionContext, { verb: string; subject: string; permissionHint: string }>> & { generic: { verb: string; subject: string; permissionHint: string } } = {
  'project.create': { verb: 'créer', subject: 'ce projet', permissionHint: 'Seuls les Owners et Admins peuvent créer des projets dans cet espace.' },
  'project.update': { verb: 'modifier', subject: 'ce projet', permissionHint: 'Seuls le créateur du projet, les Owners et les Admins peuvent le modifier.' },
  'project.delete': { verb: 'supprimer', subject: 'ce projet', permissionHint: 'Seuls le créateur du projet, les Owners et les Admins peuvent le supprimer.' },
  'project.move': { verb: 'déplacer', subject: 'ce projet', permissionHint: 'Seuls le créateur du projet, les Owners et les Admins peuvent le déplacer entre les espaces.' },
  'project.reorder': { verb: 'réorganiser', subject: 'les projets', permissionHint: 'Seuls le créateur du projet, les Owners et les Admins peuvent réorganiser les projets.' },
  'status.create': { verb: 'créer', subject: 'cette colonne', permissionHint: 'Vous devez avoir un accès complet (Full Access) au projet pour ajouter une colonne. Demandez l\'accès au créateur du projet ou à un Admin.' },
  'status.update': { verb: 'modifier', subject: 'cette colonne', permissionHint: 'Vous devez avoir un accès complet (Full Access) au projet pour modifier ses colonnes. Demandez l\'accès au créateur du projet ou à un Admin.' },
  'status.delete': { verb: 'supprimer', subject: 'cette colonne', permissionHint: 'Vous devez avoir un accès complet (Full Access) au projet pour supprimer une colonne. Demandez l\'accès au créateur du projet ou à un Admin.' },
  'status.reorder': { verb: 'réorganiser', subject: 'les colonnes', permissionHint: 'Vous devez avoir un accès complet (Full Access) au projet pour réorganiser ses colonnes. Demandez l\'accès au créateur du projet ou à un Admin.' },
  'task.create': { verb: 'créer', subject: 'une tâche', permissionHint: 'Vous devez être membre du projet avec les droits d\'édition pour créer une tâche.' },
  'task.update': { verb: 'modifier', subject: 'cette tâche', permissionHint: 'Vous devez être membre du projet avec les droits d\'édition pour la modifier.' },
  'task.delete': { verb: 'supprimer', subject: 'cette tâche', permissionHint: 'Seul le créateur de la tâche, le créateur du projet, un Admin ou un Owner peut la supprimer.' },
  'task.move': { verb: 'déplacer', subject: 'cette tâche', permissionHint: 'Vous devez être membre du projet avec les droits d\'édition pour la déplacer.' },
  'member.manage': { verb: 'gérer', subject: 'les membres', permissionHint: 'Seuls les Owners et Admins peuvent gérer les membres.' },
  'space.manage': { verb: 'gérer', subject: 'cet espace', permissionHint: 'Seuls les Owners et Admins peuvent gérer les espaces.' },
  'document.create': { verb: 'créer', subject: 'ce document', permissionHint: 'Vous devez avoir les droits d\'édition à cet emplacement pour créer un document.' },
  'document.update': { verb: 'modifier', subject: 'ce document', permissionHint: 'Vous devez avoir les droits d\'édition sur ce document pour le modifier.' },
  'document.delete': { verb: 'supprimer', subject: 'ce document', permissionHint: 'Seul le créateur du document, un Admin ou un Owner peut le supprimer.' },
  generic: { verb: 'effectuer', subject: 'cette action', permissionHint: 'Votre rôle actuel ne vous donne pas la permission nécessaire. Contactez un Admin ou le créateur de la ressource pour obtenir l\'accès.' },
};

// Étiquettes pour les nouveaux contextes ajoutés.
const EXTRA_LABELS: Partial<Record<ActionContext, { verb: string; subject: string; permissionHint: string }>> = {
  'wiki.create': { verb: 'créer', subject: 'cette page wiki', permissionHint: 'Vous devez avoir les droits d\'édition sur cet espace pour créer une page wiki.' },
  'wiki.update': { verb: 'modifier', subject: 'cette page wiki', permissionHint: 'Vous devez avoir les droits d\'édition sur cette page pour la modifier.' },
  'wiki.delete': { verb: 'supprimer', subject: 'cette page wiki', permissionHint: 'Seul le créateur de la page, un Admin ou un Owner peut la supprimer.' },
  'timesheet.create': { verb: 'saisir', subject: 'ce temps', permissionHint: 'Vous devez être assigné à la tâche (ou être Admin/Owner) pour y logger du temps.' },
  'timesheet.update': { verb: 'modifier', subject: 'cette entrée de temps', permissionHint: 'Seul l\'auteur de l\'entrée peut la modifier, sauf si elle est déjà approuvée.' },
  'timesheet.delete': { verb: 'supprimer', subject: 'cette entrée de temps', permissionHint: 'Seul l\'auteur de l\'entrée, un Admin ou un Owner peut la supprimer.' },
  'timesheet.approve': { verb: 'approuver', subject: 'cette feuille de temps', permissionHint: 'Seuls les Owners et Admins peuvent approuver les feuilles de temps.' },
  'timesheet.reject': { verb: 'rejeter', subject: 'cette feuille de temps', permissionHint: 'Seuls les Owners et Admins peuvent rejeter les feuilles de temps.' },
  'event.create': { verb: 'créer', subject: 'cet événement', permissionHint: 'Vous devez être membre du workspace pour créer un événement.' },
  'event.update': { verb: 'modifier', subject: 'cet événement', permissionHint: 'Seul l\'organisateur, un Admin ou un Owner peut modifier cet événement.' },
  'event.delete': { verb: 'supprimer', subject: 'cet événement', permissionHint: 'Seul l\'organisateur, un Admin ou un Owner peut supprimer cet événement.' },
  'event.rsvp': { verb: 'répondre à', subject: 'cet événement', permissionHint: 'Vous devez être invité à cet événement pour pouvoir y répondre.' },
  'chat.message.send': { verb: 'envoyer', subject: 'ce message', permissionHint: 'Vous devez être membre du canal pour y écrire. Pour un canal public, vous devez être membre de l\'espace associé.' },
  'chat.message.update': { verb: 'modifier', subject: 'ce message', permissionHint: 'Seul l\'auteur du message peut le modifier.' },
  'chat.message.delete': { verb: 'supprimer', subject: 'ce message', permissionHint: 'Seul l\'auteur du message, un Admin ou un Owner peut le supprimer.' },
  'chat.channel.manage': { verb: 'gérer', subject: 'ce canal', permissionHint: 'Seuls les Owners, Admins, ou le créateur du canal peuvent le gérer.' },
  'team.manage': { verb: 'gérer', subject: 'cette équipe', permissionHint: 'Seuls les Owners et Admins peuvent gérer les équipes.' },
  'expense.create': { verb: 'créer', subject: 'cette dépense', permissionHint: 'Vous devez être membre du projet pour y ajouter une dépense.' },
  'expense.update': { verb: 'modifier', subject: 'cette dépense', permissionHint: 'Seul l\'auteur de la dépense, un Admin ou un Owner peut la modifier.' },
  'expense.delete': { verb: 'supprimer', subject: 'cette dépense', permissionHint: 'Seul l\'auteur de la dépense, un Admin ou un Owner peut la supprimer.' },
  'invitation.send': { verb: 'envoyer', subject: 'cette invitation', permissionHint: 'Seuls les Owners et Admins peuvent inviter de nouveaux membres.' },
  'invitation.revoke': { verb: 'révoquer', subject: 'cette invitation', permissionHint: 'Seuls les Owners et Admins peuvent révoquer une invitation.' },
  'role.assign': { verb: 'modifier', subject: 'ce rôle', permissionHint: 'Seuls les Owners et Admins peuvent modifier les rôles. Le rôle Owner ne peut être attribué que par un autre Owner.' },
  'permission.update': { verb: 'modifier', subject: 'cette permission', permissionHint: 'Seuls les Owners, Admins ou le créateur de la ressource peuvent modifier les permissions.' },
};

// Fusionne les labels supplémentaires dans la table principale au chargement.
Object.assign(ACTION_LABELS, EXTRA_LABELS);

/**
 * Normalise une erreur (Supabase, fetch, etc.) en titre + description en français.
 */
export function normalizeActionError(err: unknown, context: ActionContext = 'generic'): NormalizedError {
  const labels = ACTION_LABELS[context] ?? ACTION_LABELS.generic;
  const raw = (err as any) ?? {};
  const code: string | undefined = raw.code;
  const status: number | undefined = raw.status ?? raw.statusCode;
  const message: string = (raw.message ?? raw.error_description ?? '').toString();
  const lower = message.toLowerCase();

  // ── Permission / RLS ──
  const isPermissionError =
    code === '42501' ||                                  // insufficient_privilege
    code === 'PGRST301' ||                               // RLS deny
    status === 401 || status === 403 ||
    lower.includes('row-level security') ||
    lower.includes('row level security') ||
    lower.includes('violates row-level') ||
    lower.includes('permission denied') ||
    lower.includes('not authorized') ||
    lower.includes('forbidden') ||
    lower.includes('accès refusé') ||
    lower.includes('non autorisé');

  if (isPermissionError) {
    return {
      title: `Impossible de ${labels.verb} ${labels.subject}`,
      description: `Vous n'avez pas la permission requise. ${labels.permissionHint}`,
      kind: 'permission',
    };
  }

  // ── Conflits / contraintes ──
  if (code === '23505' || lower.includes('duplicate key')) {
    return {
      title: 'Conflit de données',
      description: 'Un élément similaire existe déjà. Veuillez utiliser un nom ou identifiant différent.',
      kind: 'conflict',
    };
  }

  if (code === '23503' || lower.includes('foreign key')) {
    return {
      title: 'Action impossible',
      description: 'Cet élément est lié à d\'autres données et ne peut pas être modifié ou supprimé directement.',
      kind: 'conflict',
    };
  }

  if (code === '23514' || lower.includes('check constraint')) {
    return {
      title: 'Données invalides',
      description: 'Une contrainte de validation a été violée. Vérifiez les valeurs saisies.',
      kind: 'conflict',
    };
  }

  // ── Réseau ──
  if (lower.includes('failed to fetch') || lower.includes('networkerror') || lower.includes('network request failed')) {
    return {
      title: 'Problème de connexion',
      description: 'Impossible de joindre le serveur. Vérifiez votre connexion internet et réessayez.',
      kind: 'network',
    };
  }

  // ── Session ──
  if (lower.includes('jwt') || lower.includes('token') || lower.includes('session')) {
    return {
      title: 'Session expirée',
      description: 'Votre session a expiré. Veuillez vous reconnecter pour continuer.',
      kind: 'session',
    };
  }

  // ── Pas trouvé ──
  if (status === 404 || code === 'PGRST116' || lower.includes('not found')) {
    return {
      title: 'Élément introuvable',
      description: 'La ressource demandée n\'existe plus ou vous n\'y avez plus accès.',
      kind: 'notFound',
    };
  }

  // ── Fallback : message brut nettoyé ──
  return {
    title: `Impossible de ${labels.verb} ${labels.subject}`,
    description: message
      ? `Une erreur inattendue est survenue : ${message}`
      : 'Une erreur inattendue est survenue. Veuillez réessayer ou contacter le support si le problème persiste.',
    kind: 'generic',
  };
}

/**
 * Affiche une modale moderne expliquant l'erreur (style ClickUp).
 * Retourne le message normalisé (pratique pour aussi le stocker dans un state).
 */
export function showActionError(err: unknown, context: ActionContext = 'generic'): NormalizedError {
  const normalized = normalizeActionError(err, context);
  const labels = ACTION_LABELS[context] ?? ACTION_LABELS.generic;
  useActionErrorStore.getState().show({
    title: normalized.title,
    description: normalized.description,
    kind: normalized.kind,
    actionLabel: context === 'generic' ? undefined : `${labels.verb} ${labels.subject}`,
  });
  return normalized;
}

// =============================================================================
// runAction — Wrapper universel pour toute mutation susceptible d'échouer.
//
// Usage :
//   await runAction(() => repo.deleteTask(id), 'task.delete');
//
// Comportement :
//   - Exécute la promesse.
//   - Si elle réussit : retourne la valeur.
//   - Si elle échoue : affiche le popin explicatif (ActionErrorDialog)
//     puis rethrow pour laisser l'appelant gérer son state local
//     (revert optimiste, etc.).
//
// Options :
//   - silent: true → n'affiche pas le popin (utile quand on veut juste
//     normaliser une erreur sans interrompre l'UI).
//   - onlyPermission: true → n'affiche le popin que pour les erreurs de
//     permission (les conflits, le réseau, etc. restent silencieux côté UI).
// =============================================================================
export interface RunActionOptions {
  silent?: boolean;
  onlyPermission?: boolean;
}

export async function runAction<T>(
  fn: () => Promise<T>,
  context: ActionContext = 'generic',
  options: RunActionOptions = {},
): Promise<T> {
  try {
    return await fn();
  } catch (err) {
    const normalized = normalizeActionError(err, context);
    const shouldShow =
      !options.silent &&
      (!options.onlyPermission || normalized.kind === 'permission');
    if (shouldShow) {
      const labels = ACTION_LABELS[context] ?? ACTION_LABELS.generic;
      useActionErrorStore.getState().show({
        title: normalized.title,
        description: normalized.description,
        kind: normalized.kind,
        actionLabel: context === 'generic' ? undefined : `${labels.verb} ${labels.subject}`,
      });
    }
    throw err;
  }
}

/**
 * Hook React Query global : appelle ceci dans `MutationCache.onError` et
 * `QueryCache.onError` côté QueryClient pour que toute erreur 403/RLS non
 * gérée par l'appelant déclenche automatiquement le popin explicatif.
 *
 * Skip si la mutation/query définit `meta: { silent: true }`.
 */
export function handleQueryError(err: unknown, meta?: Record<string, unknown> | undefined) {
  if (meta?.silent === true) return;
  const context = (meta?.actionContext as ActionContext | undefined) ?? 'generic';
  const normalized = normalizeActionError(err, context);
  // Par défaut, l'intercepteur global n'affiche QUE les erreurs de permission
  // ou de session pour ne pas noyer l'utilisateur sous des popins génériques.
  // Les hooks qui veulent un popin systématique doivent appeler runAction
  // explicitement.
  if (normalized.kind !== 'permission' && normalized.kind !== 'session') return;
  const labels = ACTION_LABELS[context] ?? ACTION_LABELS.generic;
  useActionErrorStore.getState().show({
    title: normalized.title,
    description: normalized.description,
    kind: normalized.kind,
    actionLabel: context === 'generic' ? undefined : `${labels.verb} ${labels.subject}`,
  });
}
